import { useState } from "react";
import apiClient, { formatApiError, clearToken } from "../api";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Lock, KeyRound, Save, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MIN_LENGTH = 8;

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrorText("");
  };

  const newLongEnough = form.new_password.length >= MIN_LENGTH;
  const newMatches =
    form.new_password.length > 0 && form.new_password === form.confirm_password;
  const newDifferent =
    form.new_password.length > 0 &&
    form.new_password !== form.current_password;

  const canSubmit =
    form.current_password.length > 0 &&
    newLongEnough &&
    newMatches &&
    newDifferent &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    if (!form.current_password) {
      setErrorText("Vul je huidige wachtwoord in.");
      return;
    }
    if (!newLongEnough) {
      setErrorText(`Het nieuwe wachtwoord moet minimaal ${MIN_LENGTH} tekens zijn.`);
      return;
    }
    if (!newMatches) {
      setErrorText("De nieuwe wachtwoorden komen niet overeen.");
      return;
    }
    if (!newDifferent) {
      setErrorText("Het nieuwe wachtwoord moet verschillen van het huidige.");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Wachtwoord gewijzigd — je wordt opnieuw ingelogd.");
      // Force a re-login: clear token and send to login.
      clearToken();
      await logout().catch(() => {});
      setTimeout(() => navigate("/admin/login", { replace: true }), 800);
    } catch (err) {
      const msg = formatApiError(err);
      setErrorText(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-account-page" className="pb-20 max-w-3xl">
      <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
        Account
      </p>
      <h1 className="font-display text-5xl text-white tracking-tight">
        Mijn account
      </h1>
      <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
        Beheer je inloggegevens. Voor de veiligheid moet je je huidige
        wachtwoord opgeven om een nieuw wachtwoord in te stellen.
      </p>

      <div className="mt-10 bg-ink-900 border border-white/[0.06] p-7 mb-6">
        <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-4">
          Profiel
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-body">
          <div>
            <dt className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">
              Naam
            </dt>
            <dd className="text-white mt-1">{user?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">
              E-mailadres
            </dt>
            <dd className="text-white mt-1">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">
              Rol
            </dt>
            <dd className="text-white mt-1 uppercase tracking-[0.15em]">
              {user?.role}
            </dd>
          </div>
          <div>
            <dt className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">
              Aangemaakt
            </dt>
            <dd className="text-white mt-1">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "—"}
            </dd>
          </div>
        </dl>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="change-password-form"
        noValidate
        className="bg-ink-900 border border-white/[0.06] p-7 flex flex-col gap-5"
      >
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-champagne font-body mb-2">
            Wachtwoord wijzigen
          </p>
          <h2 className="font-display text-2xl text-white tracking-tight">
            Nieuw wachtwoord instellen
          </h2>
        </div>

        <Field
          label="Huidig wachtwoord"
          icon={<Lock size={14} />}
          testId="current-password"
          type="password"
          value={form.current_password}
          onChange={(v) => set("current_password", v)}
          autoComplete="current-password"
        />

        <Field
          label="Nieuw wachtwoord"
          icon={<KeyRound size={14} />}
          testId="new-password"
          type="password"
          value={form.new_password}
          onChange={(v) => set("new_password", v)}
          autoComplete="new-password"
          hint={`Minimaal ${MIN_LENGTH} tekens.`}
        />

        <Field
          label="Bevestig nieuw wachtwoord"
          icon={<KeyRound size={14} />}
          testId="confirm-password"
          type="password"
          value={form.confirm_password}
          onChange={(v) => set("confirm_password", v)}
          autoComplete="new-password"
        />

        {/* Live validation hints */}
        <ul className="flex flex-col gap-2 mt-1">
          <Hint ok={newLongEnough}>
            Minimaal {MIN_LENGTH} tekens
          </Hint>
          <Hint ok={newMatches}>Nieuwe wachtwoorden komen overeen</Hint>
          <Hint ok={newDifferent}>
            Verschilt van het huidige wachtwoord
          </Hint>
        </ul>

        {errorText && (
          <div
            data-testid="change-password-error"
            className="bg-red-950/40 border border-red-900/50 px-4 py-3 text-sm text-red-300 font-body flex items-start gap-3"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{errorText}</span>
          </div>
        )}

        <div className="flex items-center gap-3 mt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="change-password-submit"
            className="inline-flex items-center gap-2 px-6 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={12} />
            {submitting ? "Bezig..." : "Wachtwoord wijzigen"}
          </button>
          <p className="text-[11px] text-zinc-500 font-body">
            Na het wijzigen word je opnieuw gevraagd in te loggen.
          </p>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, icon, type, value, onChange, hint, testId, autoComplete }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
      {label}
    </span>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
          {icon}
        </span>
      )}
      <Input
        data-testid={testId}
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className={`${icon ? "pl-10" : ""} h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-body text-white rounded-none`}
      />
    </div>
    {hint && <span className="text-[11px] text-zinc-600 font-body">{hint}</span>}
  </label>
);

const Hint = ({ ok, children }) => (
  <li
    className={`flex items-center gap-2 text-[11px] font-body ${
      ok ? "text-champagne" : "text-zinc-500"
    }`}
  >
    <span
      className={`w-4 h-4 inline-flex items-center justify-center border ${
        ok ? "border-champagne" : "border-white/15"
      }`}
    >
      {ok && <Check size={10} />}
    </span>
    {children}
  </li>
);

export default AccountPage;
